import getQueryParams from 'src/functions/getQueryParams';
import { getSessionStorage, setSessionStorage } from 'src/functions/localStorageHandler';

const FEATURE_FLAGS = {
  i18n_debug: {
    enabled: false,
    description: 'Enables debug logging for i18n system',
  },
  free_trial_email_modal: {
    enabled: false,
    description: 'Enables the free trial email modal',
  },
  pro_services_page_branded_apps: {
    enabled: true,
    description: 'Enables the Pro Services page with branded apps content',
  },
  momentum_article_improvements: {
    enabled: true,
    description: 'Enables improvements to the Momentum article page',
  },
  unlimited_spaces: {
    enabled: true,
    description: 'Enables unlimited spaces content on the website',
  },
  pricing_unlimited_spaces_explore_q2_26: {
    enabled: true,
    description: 'Enables the redesigned Pricing page experiment (Compare Plans hero, 3-tier layout with Mighty Pro dark theme, Revenue Calculator dark variant)',
  },
  api_driven_hubspot_forms: {
    enabled: false,
    description: 'Enables API-driven HubSpot form submissions instead of embedded HubSpot forms',
  },
};

type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

const getDefaultValue = (featureName: FeatureFlagKey): boolean => FEATURE_FLAGS[featureName]?.enabled;

const getFeatureFlagValue = (featureName: FeatureFlagKey): boolean => {
  const sessionStorageValue = getSessionStorage(getSessionStorageKey(featureName));

  if (sessionStorageValue) {
    return sessionStorageValue === 'true';
  }

  return getDefaultValue(featureName);
};

const getSessionStorageKey = (featureName: FeatureFlagKey): string => `@featureFlag:${featureName}`;

const storeSessionOverride = (featureName: FeatureFlagKey, enabled: boolean): void => {
  const sessionStorageKey = getSessionStorageKey(featureName);

  setSessionStorage(sessionStorageKey, enabled.toString());
};

const useFeatureFlag = (featureName: FeatureFlagKey): boolean => {
  // Gets default value for the feature flag on build time
  if (typeof window === 'undefined') return getDefaultValue(featureName);

  const { ff_on: featureFlagOnOverride, ff_off: featureFlagOffOverride } = getQueryParams();

  // Override feature flag value and store it in the local storage
  if (featureFlagOnOverride && decodeURIComponent(featureFlagOnOverride) === featureName) {
    storeSessionOverride(featureName, true);
  }

  if (featureFlagOffOverride && decodeURIComponent(featureFlagOffOverride) === featureName) {
    storeSessionOverride(featureName, false);
  }

  return getFeatureFlagValue(featureName);
};

export default useFeatureFlag;
